import * as React from "react"
import TextField from "@mui/material/TextField"
import Autocomplete from "@mui/material/Autocomplete"
import CircularProgress from "@mui/material/CircularProgress"
import { useTranslation } from "react-i18next"
import { namespaces } from "../../../i18n/i18n.constants"
import { DistrictInterface } from "../../interfaces/district.interface"
import { getDistrictAPI } from "../../../api/district.api"

type DistrictSelectAsyncComponentProps = {
   multiple?: boolean
   disabled: boolean
   value: DistrictInterface
   variant?: "standard" | "filled" | "outlined"
   error: boolean
   provinceId?: string
   helperText: string | undefined
   InputProps?: any
   isMarginNormal?: boolean
   onInputChange: (value: DistrictInterface) => void
}

export const DistrictSelectAsyncComponent = (
   props: DistrictSelectAsyncComponentProps
) => {
   const { t } = useTranslation(namespaces.common)

   const [open, setOpen] = React.useState(false)
   const [options, setOptions] = React.useState<readonly DistrictInterface[]>(
      []
   )
   const loading = open && options.length === 0

   React.useEffect(() => {
      if (!loading) {
         return undefined
      }

      ;(async () => {
         getDistrictAPI(props.provinceId ? props.provinceId : "")
            .then((res) => {
               if (!res.data || res.data.length === 0) {
                  setOpen(false)
               } else {
                  const optionData = res.data.map((e: any) => {
                     const district: DistrictInterface = {
                        id: e.id,
                        name: e.name,
                     }
                     return district
                  })
                  setOptions(optionData)
               }
            })
            .catch((err) => {
               setOpen(false)
            })
      })()
   }, [loading, props.provinceId])

   React.useEffect(() => {
      if (!open) {
         setOptions([])
      }
   }, [open])

   return (
      <Autocomplete
         id="asynchronous-district"
         multiple={props.multiple ? props.multiple : false}
         open={open}
         onOpen={() => {
            if (props.provinceId) {
               setOpen(true)
            }
         }}
         onClose={() => {
            setOpen(false)
         }}
         onChange={(event: any, newValue: any | null) => {
            props.onInputChange(newValue)
         }}
         value={props.value ? props.value : null}
         isOptionEqualToValue={(option, value) => option.name === value.name}
         getOptionLabel={(option) => option.name}
         options={options}
         loading={loading}
         disabled={props.disabled}
         clearIcon={null}
         noOptionsText={t("dialog.noOption")}
         renderOption={(htmlProps, option) => {
            return (
               <li {...htmlProps} key={option.id}>
                  {option.name}
               </li>
            )
         }}
         readOnly={props.InputProps?.readOnly ? true : false}
         renderInput={(params) => (
            <TextField
               {...params}
               margin={props.isMarginNormal ? "normal" : "none"}
               fullWidth
               variant={props.variant}
               error={props.error}
               helperText={props.helperText}
               label={t("field.district")}
               InputProps={{
                  ...params.InputProps,
                  ...props.InputProps,
                  endAdornment: (
                     <React.Fragment>
                        {loading ? (
                           <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                     </React.Fragment>
                  ),
               }}
            />
         )}
      />
   )
}
